/**
 * SEC-1 · Validación server-authoritative de lo que llega por el socket.
 * DOM-free y testeable sin red.
 *
 * Principio: **nada de lo que manda el cliente se cree tal cual**. El socket es
 * texto arbitrario; `JSON.parse` solo garantiza que es JSON, no que sea un
 * `InputCommand`. Aquí se cubren las cuatro puertas del backlog:
 *
 *  - **rango**  → `validateInputCommand`: el tick es entero finito y dentro de
 *    la ventana viva; `coerceInput` reduce el input a seis booleanos reales
 *    (nada de `up: 1e9`, `up: {}` ni getters con efectos).
 *  - **velocidad** → la garantía dura ya la da `AuthoritativeServer.step()`
 *    (un `step()` = un tick, pase lo que pase); aquí se le suma el tope de
 *    ticks futuros encolables, para que nadie reserve memoria del servidor.
 *  - **rate**   → `ConnectionLimiter`: cubos de tokens por TIPO de mensaje.
 *    Un cliente honesto a 60 Hz cabe holgado; un flood se descarta sin CPU.
 *  - **sanity** → `sanitizeGameState`: si la física se va a NaN/infinito (bug o
 *    input malicioso), se detecta y se recentra en vez de propagar veneno a
 *    todos los snapshots de la sala.
 *
 * Todo lo que se rechaza se DESCARTA en silencio (no se responde con error):
 * contestar convierte al servidor en amplificador y le regala al atacante un
 * oráculo de qué acepta.
 */
import { GameState, InputState, MAPS, ANALOG_SCALE } from "../game";
import { MAX_CHAT, MAX_NAME } from "../moderation";
import { InputCommand } from "./protocol";
import { ClientMessage, ControlAction, ReportReason } from "./wire";

/** Tamaño máximo de un mensaje de cliente (bytes). Un input JSON ronda 120 B. */
export const MAX_MESSAGE_BYTES = 2048;

/** Tope de ticks futuros que un cliente puede tener encolados a la vez. */
export const MAX_QUEUED_INPUTS = 64;

/** Límites de reglas de partido aceptables desde el cliente. */
export const MAX_GOAL_LIMIT = 99;
export const MAX_TIME_LIMIT = 3600;

/** Slots posibles de una sala (coincide con la capacidad máxima del matchmaker). */
export const MAX_SLOT = 30;

const CONTROL_ACTIONS: ReadonlySet<string> = new Set<ControlAction>([
  "start", "pause", "resume", "finish", "restart",
  "setTeam", "kick", "transferAdmin", "grantAdmin", "revokeAdmin", "setTeamNames",
  "toSpectator", "toPlayer",
]);

/** SEC-3 · Motivos de reporte aceptados (lista cerrada: ver `ReportReason`). */
const REPORT_REASONS: ReadonlySet<string> = new Set<ReportReason>([
  "cheating", "abuse", "spam", "other",
]);

/** Entero finito dentro de [min, max]. Rechaza NaN, Infinity y decimales. */
export function isIntInRange(v: unknown, min: number, max: number): boolean {
  return typeof v === "number" && Number.isInteger(v) && v >= min && v <= max;
}

/**
 * Reduce cualquier cosa a un `InputState` de seis booleanos estrictos. Es la
 * frontera: a partir de aquí el sim solo ve `true`/`false`, así que un
 * `{up: 999}` no puede multiplicar fuerzas ni un `{up: {valueOf}}` ejecutar
 * código dentro del hot path.
 */
export function coerceInput(raw: unknown): InputState {
  const o = (raw ?? {}) as Record<string, unknown>;
  return {
    up: o.up === true,
    down: o.down === true,
    left: o.left === true,
    right: o.right === true,
    kick: o.kick === true,
    kickRelease: o.kickRelease === true,
    // Ejes analógicos del mando táctil: enteros acotados al círculo del stick.
    // Se redondean aquí (y no en el sim) porque el sim tiene que poder confiar
    // en que lo que le llega ya es un número sano: un `mx` fraccionario abriría
    // una divergencia entre cliente y servidor, y un `mx` de 1e9 mandaría el
    // disco al infinito de un tick.
    mx: axis(o.mx),
    my: axis(o.my),
  };
}

/** Un eje analógico saneado: entero en [-ANALOG_SCALE, ANALOG_SCALE]. */
function axis(v: unknown): number {
  if (typeof v !== "number" || !Number.isFinite(v)) return 0;
  return Math.max(-ANALOG_SCALE, Math.min(ANALOG_SCALE, Math.round(v)));
}

/**
 * Valida un `InputCommand` crudo contra el reloj del servidor y lo devuelve
 * normalizado, o `null` si es inaplicable.
 *
 * `serverTick`/`maxLead` describen la ventana viva: un input del pasado ya no
 * se puede aplicar (el tick se simuló) y uno demasiado futuro es el vector
 * clásico de speedhack (adelantar el reloj para simular de más).
 */
export function validateInputCommand(
  raw: unknown,
  serverTick: number,
  maxLead: number,
): InputCommand | null {
  if (!raw || typeof raw !== "object") return null;
  const cmd = raw as { tick?: unknown; input?: unknown };
  // Tope absoluto además de la ventana: un tick de 1e18 desbordaría cualquier
  // aritmética posterior aunque la ventana lo dejara pasar.
  if (!isIntInRange(cmd.tick, 0, Number.MAX_SAFE_INTEGER)) return null;
  const tick = cmd.tick as number;
  if (tick < serverTick) return null;
  if (tick > serverTick + maxLead) return null;
  return { tick, input: coerceInput(cmd.input) };
}

/**
 * Valida (y normaliza) un mensaje de cliente ya parseado. Devuelve el mensaje
 * saneado o `null` si no es aplicable. No decide PERMISOS (eso es del admin en
 * `GameRoom`), solo forma y rango.
 */
export function validateClientMessage(raw: unknown): ClientMessage | null {
  if (!raw || typeof raw !== "object") return null;
  const m = raw as Record<string, unknown>;

  if (m.t === "input") {
    // El rango del tick depende del reloj del servidor y se comprueba después,
    // en `validateInputCommand`; aquí solo la forma.
    const cmd = m.cmd as { tick?: unknown; input?: unknown } | undefined;
    if (!cmd || typeof cmd !== "object") return null;
    if (!isIntInRange(cmd.tick, 0, Number.MAX_SAFE_INTEGER)) return null;
    return { t: "input", cmd: { tick: cmd.tick as number, input: coerceInput(cmd.input) } };
  }

  if (m.t === "ping") {
    // RTT reportado: telemetría, pero un valor absurdo ensuciaría el roster y
    // las métricas de latencia. Se acota a algo físicamente posible.
    if (typeof m.ms !== "number" || !Number.isFinite(m.ms)) return null;
    return { t: "ping", ms: Math.max(0, Math.min(60000, m.ms)) };
  }

  if (m.t === "chat") {
    // Solo forma y tamaño: el CONTENIDO lo modera la sala (SEC-2), que es quien
    // sabe enmascarar insultos y decidir si el mensaje queda en nada. Aquí se
    // recorta con holgura sobre `MAX_CHAT` para que la moderación trabaje sobre
    // el texto real (los invisibles que quita no deben "gastar" longitud) sin
    // que un mensaje de un megabyte llegue siquiera a procesarse.
    if (typeof m.text !== "string") return null;
    return { t: "chat", text: m.text.slice(0, MAX_CHAT * 4) };
  }

  if (m.t === "typing") {
    // Un booleano y nada más: el borrador no viaja (ver `wire.ts`).
    if (typeof m.on !== "boolean") return null;
    return { t: "typing", on: m.on };
  }

  if (m.t === "profile") {
    // Forma y tamaño; el nombre lo modera la sala (SEC-2), que es quien sabe
    // enmascarar y decidir. El icono es un ID corto de catálogo: el servidor no
    // conoce el catálogo del cliente, así que lo único que garantiza es que sea
    // un identificador inofensivo — el dibujo lo elige quien renderiza, y un id
    // desconocido simplemente no pinta nada.
    const out: ClientMessage = { t: "profile" };
    if (m.name !== undefined) {
      if (typeof m.name !== "string") return null;
      out.name = m.name.slice(0, MAX_NAME * 4);
    }
    if (m.icon !== undefined) {
      if (typeof m.icon !== "string" || !/^[a-z0-9-]{0,16}$/.test(m.icon)) return null;
      out.icon = m.icon;
    }
    if (out.name === undefined && out.icon === undefined) return null;
    return out;
  }

  if (m.t === "report") {
    if (!isIntInRange(m.targetSlot, 0, MAX_SLOT - 1)) return null;
    if (typeof m.reason !== "string" || !REPORT_REASONS.has(m.reason)) return null;
    return { t: "report", targetSlot: m.targetSlot as number, reason: m.reason as ReportReason };
  }

  if (m.t === "control") {
    if (typeof m.action !== "string" || !CONTROL_ACTIONS.has(m.action)) return null;
    const out: ClientMessage = { t: "control", action: m.action as ControlAction };
    if (m.mapId !== undefined) {
      if (typeof m.mapId !== "string" || !MAPS.some((x) => x.id === m.mapId)) return null;
      out.mapId = m.mapId;
    }
    if (m.goalLimit !== undefined) {
      if (!isIntInRange(m.goalLimit, 0, MAX_GOAL_LIMIT)) return null;
      out.goalLimit = m.goalLimit as number;
    }
    if (m.timeLimit !== undefined) {
      if (!isIntInRange(m.timeLimit, 0, MAX_TIME_LIMIT)) return null;
      out.timeLimit = m.timeLimit as number;
    }
    if (m.targetSlot !== undefined) {
      if (!isIntInRange(m.targetSlot, 0, MAX_SLOT - 1)) return null;
      out.targetSlot = m.targetSlot as number;
    }
    if (m.team !== undefined) {
      if (m.team !== 0 && m.team !== 1 && m.team !== "spec") return null;
      out.team = m.team as 0 | 1 | "spec";
    }
    if (m.teamNames !== undefined) {
      const n = m.teamNames;
      if (!Array.isArray(n) || n.length !== 2) return null;
      if (typeof n[0] !== "string" || typeof n[1] !== "string") return null;
      // El contenido lo modera la sala (SEC-2); aquí solo se acota el tamaño
      // para que un nombre de un megabyte no llegue siquiera a procesarse.
      out.teamNames = [(n[0] as string).slice(0, 64), (n[1] as string).slice(0, 64)];
    }
    return out;
  }

  return null;
}

/**
 * Cubo de tokens (rate limiting). `capacity` es la ráfaga tolerada y
 * `refillPerSec` el caudal sostenido. Sin `setInterval`: el relleno se calcula
 * con el reloj que se le pase, así el test es determinista.
 */
export class TokenBucket {
  private tokens: number;
  private last: number;

  constructor(
    readonly capacity: number,
    readonly refillPerSec: number,
    now = 0,
  ) {
    this.tokens = capacity;
    this.last = now;
  }

  /** Consume un token si queda. `false` = hay que descartar el mensaje. */
  allow(now: number, cost = 1): boolean {
    const dt = Math.max(0, (now - this.last) / 1000);
    this.last = now;
    this.tokens = Math.min(this.capacity, this.tokens + dt * this.refillPerSec);
    if (this.tokens < cost) return false;
    this.tokens -= cost;
    return true;
  }

  /** Tokens disponibles (para tests/diagnóstico). */
  get available(): number {
    return this.tokens;
  }
}

/** Presupuestos por tipo de mensaje: ráfaga tolerada / caudal sostenido (por segundo). */
export const RATE_LIMITS = {
  // 60 Hz de inputs + redundancia del jitter-buffer: 120/s sostenido va sobrado
  // para un cliente honesto y corta un flood de miles por segundo.
  input: { capacity: 240, perSec: 120 },
  // El control lo dispara un humano pulsando botones.
  control: { capacity: 20, perSec: 5 },
  // El cliente reporta su ping ~1/s.
  ping: { capacity: 10, perSec: 2 },
  // Chat: lo teclea una persona. Una ráfaga de 6 cubre pegar varias líneas
  // seguidas; 1/s sostenido es más de lo que nadie escribe y corta el spam.
  chat: { capacity: 6, perSec: 1 },
  // "Escribiendo": dos mensajes por composición (abrir y cerrar). Se toca más a
  // menudo que el chat —se abre y se cierra sin llegar a escribir— pero cuesta
  // un booleano, así que el presupuesto es holgado y el flood sigue cortado.
  typing: { capacity: 12, perSec: 3 },
  // Perfil: se cambia a mano y de tarde en tarde. Ráfaga corta para probar dos
  // o tres iconos seguidos; sostenido bajo, porque cada cambio difunde roster.
  profile: { capacity: 5, perSec: 0.5 },
  // Reportes: se hacen a mano y muy de vez en cuando. El presupuesto ajustado
  // es parte de la función — un reporte tiene valor porque cuesta, y quien pueda
  // mandar cientos convierte la cola de moderación en ruido.
  report: { capacity: 3, perSec: 0.05 },
} as const;

/**
 * Limitador por conexión: un cubo por tipo de mensaje. Lo instancia el
 * transporte (una por socket) y lo consulta antes de tocar la sala, de modo que
 * un cliente abusivo no gasta CPU de simulación ni memoria de la sala.
 */
export class ConnectionLimiter {
  /** Un cubo por tipo de mensaje. Se indexa por el propio `t` del mensaje, de
   *  modo que añadir un tipo nuevo al protocolo sin darle presupuesto rompe la
   *  compilación en vez de colarse sin límite. */
  private readonly buckets: Record<ClientMessage["t"], TokenBucket>;
  /** Mensajes descartados por este cliente (señal de cheat/bug para OBS-2). */
  dropped = 0;

  constructor(now = 0) {
    const make = (l: { capacity: number; perSec: number }): TokenBucket =>
      new TokenBucket(l.capacity, l.perSec, now);
    this.buckets = {
      input: make(RATE_LIMITS.input),
      control: make(RATE_LIMITS.control),
      ping: make(RATE_LIMITS.ping),
      chat: make(RATE_LIMITS.chat),
      typing: make(RATE_LIMITS.typing),
      profile: make(RATE_LIMITS.profile),
      report: make(RATE_LIMITS.report),
    };
  }

  /** ¿Cabe este mensaje en el presupuesto del cliente? */
  allow(kind: ClientMessage["t"], now: number): boolean {
    const bucket = this.buckets[kind];
    // Tipo desconocido (mensaje que pasó el parser pero no tiene presupuesto):
    // se descarta. No debería ocurrir — el tipo de `buckets` lo impide.
    if (!bucket) { this.dropped++; return false; }
    const ok = bucket.allow(now);
    if (!ok) this.dropped++;
    return ok;
  }
}

/**
 * Puerta del transporte: tamaño → JSON → forma/rango. Devuelve el mensaje
 * saneado o `null` (descartar en silencio). El presupuesto por cliente NO se
 * mira aquí: eso necesita saber QUIÉN manda, y de eso sabe `GameRoom`.
 */
export function parseClientMessage(raw: string): ClientMessage | null {
  if (typeof raw !== "string" || raw.length > MAX_MESSAGE_BYTES) return null;
  try {
    return validateClientMessage(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Red de seguridad de la simulación: si algún cuerpo acaba con posición o
 * velocidad no finita, o disparado lejísimos de la cancha, se recentra y se
 * frena. Un solo NaN se propaga a TODOS los cuerpos en el siguiente `step()` y
 * deja la sala inservible para los dos jugadores; es más barato corregirlo que
 * arrastrarlo.
 *
 * Devuelve cuántos cuerpos hubo que corregir (0 = todo sano). El servidor lo
 * publica como métrica: si deja de ser 0, hay un bug de física que investigar.
 */
export function sanitizeGameState(g: GameState): number {
  const w = g.world;
  // Margen generoso: fuera de este cuadro no hay juego posible, solo desbordes.
  const lim = Math.max(g.courtW, g.courtH) * 4;
  const maxSpeed = 100000;
  let fixed = 0;
  for (let i = 0; i < w.count; i++) {
    const bad =
      !Number.isFinite(w.px[i]) || !Number.isFinite(w.py[i]) ||
      !Number.isFinite(w.vx[i]) || !Number.isFinite(w.vy[i]) ||
      Math.abs(w.px[i]) > lim || Math.abs(w.py[i]) > lim ||
      Math.abs(w.vx[i]) > maxSpeed || Math.abs(w.vy[i]) > maxSpeed;
    if (!bad) continue;
    w.px[i] = g.courtW / 2;
    w.py[i] = g.courtH / 2;
    w.vx[i] = 0;
    w.vy[i] = 0;
    fixed++;
  }
  return fixed;
}
